import { FormField } from "@/lib/database/forms";

/** Reads a field's answer, unwrapping the {value} shape used by "Other" options. */
function answerValue(raw: unknown): unknown {
  return typeof raw === "object" && raw !== null ? (raw as { value?: unknown }).value : raw;
}

/**
 * Maps each section name to the respondent-type values gating it, derived from
 * whichever fields in that section explicitly carry a respondent_type `show_if` —
 * built once per {@link computeVisibleFields} call, not per field, to stay O(n).
 * A field with NO `show_if` of its own but sharing a gated section inherits this
 * gate automatically ({@link isShownByShowIf}), so adding a field to an already-gated
 * section doesn't require separately re-tagging it. Sections where tagged fields
 * disagree on the gate resolve to no section-level gate — each field falls back to
 * its own explicit `show_if`, avoiding an ambiguous/silent merge.
 */
function buildSectionRespondentTypeGates(formFields: FormField[]): Map<string, number[]> {
  const bySection = new Map<string, number[][]>();
  for (const field of formFields) {
    if (
      !field.section ||
      !field.show_if ||
      !("source" in field.show_if) ||
      field.show_if.source !== "respondent_type"
    ) {
      continue;
    }
    const existing = bySection.get(field.section) ?? [];
    existing.push(field.show_if.values);
    bySection.set(field.section, existing);
  }

  const gates = new Map<string, number[]>();
  for (const [section, valueSets] of bySection) {
    const [first, ...rest] = valueSets;
    const allMatch = rest.every((v) => v.length === first.length && v.every((id) => first.includes(id)));
    if (allMatch) gates.set(section, first);
  }
  return gates;
}

/**
 * Whether a field's `show_if` reveal condition is satisfied.
 *
 * Two condition sources:
 * - Answer-based (legacy/default shape, has `field_id`): the controlling field's
 *   current answer is one of the listed values. A single-select match is a direct
 *   comparison; a multiselect matches if any chosen value is listed. An unanswered
 *   or off-list controlling answer keeps the field hidden. A missing/misconfigured
 *   reference reveals the field (fail open) rather than trapping it.
 * - Respondent-type-based (`source: "respondent_type"`, or inherited from the
 *   field's section via `sectionGates`): the viewer's resolved respondent type id is
 *   one of the listed values. No resolved type (unverified visitor, or a form with
 *   no respondent types) keeps the field hidden — this condition never fails open,
 *   since it can gate real pricing.
 */
function isShownByShowIf(
  field: FormField,
  fieldsById: Map<string, FormField>,
  formData: Record<string, unknown>,
  respondentTypeId: number | null | undefined,
  sectionGates: Map<string, number[]>
): boolean {
  if (!field.show_if) {
    const inheritedGate = field.section ? sectionGates.get(field.section) : undefined;
    if (!inheritedGate) return true;
    return respondentTypeId != null && inheritedGate.includes(respondentTypeId);
  }
  const showIf = field.show_if;

  if ("source" in showIf) {
    return respondentTypeId != null && showIf.values.includes(respondentTypeId);
  }

  const controller = fieldsById.get(showIf.field_id);
  if (!controller) return true;
  const raw = formData[controller.label];
  if (Array.isArray(raw)) {
    return raw.some((v) => showIf.values.includes(String(answerValue(v))));
  }
  const value = answerValue(raw);
  return value != null && showIf.values.includes(String(value));
}

/**
 * Computes which form fields are visible given the current answers, applying
 * each field's `conditional_logic` (skip-to-section / skip-to-end).
 *
 * A field stays visible by default and only hides when its trigger condition is
 * actually met: selecting `condition_value` either skips forward to
 * `skip_to_section` (hiding the fields in between) or to the end of the form
 * (`skip_to_end`). An unanswered or non-trigger answer hides nothing — that's
 * the intuitive behavior (an earlier version hid every field after an unanswered
 * conditional, which made long forms look truncated).
 *
 * @param formFields - All fields in form order
 * @param formData - Current answers keyed by field label
 * @param respondentTypeId - The viewer's resolved respondent type, if any — drives
 *   `show_if` conditions sourced from respondent type rather than another answer.
 *   Undefined/null means no type resolved (e.g. unverified visitor, or the form has
 *   no respondent types), so respondent_type-gated fields/sections stay hidden.
 * @returns The fields that should render, in order
 */
export function computeVisibleFields(
  formFields: FormField[],
  formData: Record<string, unknown>,
  respondentTypeId?: number | null
): FormField[] {
  const visible: FormField[] = [];
  let hideRemainingFields = false;
  let skipToSection: string | null = null;
  // Resolve show_if controllers by id in O(1) instead of scanning per field.
  const fieldsById = new Map(formFields.map((f) => [f.id, f]));
  const sectionGates = buildSectionRespondentTypeGates(formFields);

  for (const field of formFields) {
    // In skip mode: drop fields until we reach the target section, then resume.
    if (skipToSection) {
      if (field.section === skipToSection) {
        skipToSection = null;
        hideRemainingFields = false;
        visible.push(field);
      }
      continue;
    }

    // Skip-to-end was triggered earlier — nothing more is visible.
    if (hideRemainingFields) break;

    // A field with a reveal condition stays hidden until that condition is met.
    if (!isShownByShowIf(field, fieldsById, formData, respondentTypeId, sectionGates)) continue;

    visible.push(field);

    if (field.conditional_logic?.condition_value) {
      const fieldValue = formData[field.label] as unknown;
      let conditionMet = false;

      if (field.type === "dropdown" || field.type === "multiplechoice") {
        const actualValue =
          typeof fieldValue === "object" && fieldValue !== null
            ? (fieldValue as { value?: unknown }).value
            : fieldValue;
        conditionMet = actualValue === field.conditional_logic.condition_value;
      }

      if (field.type === "multiselect" && Array.isArray(fieldValue)) {
        conditionMet = fieldValue.some((v) => {
          const actualValue = typeof v === "object" && v !== null ? (v as { value?: unknown }).value : v;
          return actualValue === field.conditional_logic?.condition_value;
        });
      }

      // Only an explicit trigger answer hides anything. Unanswered / non-trigger
      // answers keep the rest of the form visible.
      if (conditionMet) {
        if (field.conditional_logic.skip_to_section) {
          skipToSection = field.conditional_logic.skip_to_section;
        } else if (field.conditional_logic.skip_to_end) {
          hideRemainingFields = true;
        }
      }
    }
  }

  return visible;
}
